import type {
  BackgroundRemovalMode,
  BackgroundRemovalRequest,
} from "@proof/core";

export type BackgroundRemovalOptions = Partial<BackgroundRemovalRequest> & {
  /** The stored original; the built-in service processes it on the server. */
  assetId?: string;
};

export type BackgroundRemovalService = {
  (
    input: Blob,
    signal: AbortSignal,
    progress?: (message: string) => void,
    options?: BackgroundRemovalOptions,
  ): Promise<Blob>;
  /** The modes this server supports; when absent every mode is offered. */
  modes?: (signal: AbortSignal) => Promise<BackgroundRemovalMode[]>;
};

/**
 * Removes backgrounds on the proof server: a solid-colour fill by default, or
 * subject detection in photo mode. Nothing is downloaded to the browser.
 */
export class BackgroundRemover {
  constructor(
    private baseUrl = "",
    private init: {
      headers?: Record<string, string>;
      credentials?: RequestCredentials;
    } = {},
  ) {}

  /** Asks the server which modes it supports; solid is always available. */
  modes = async (signal: AbortSignal): Promise<BackgroundRemovalMode[]> => {
    const base = this.baseUrl.replace(/\/+$/, "");
    const response = await fetch(`${base}/api/background-removal`, {
      headers: { ...this.init.headers },
      credentials: this.init.credentials || "same-origin",
      redirect: "error",
      signal,
    });
    if (!response.ok) return ["solid", "photo"];
    const body = (await response.json().catch(() => undefined)) as
      { modes?: unknown } | undefined;
    return Array.isArray(body?.modes) && body.modes.includes("photo")
      ? ["solid", "photo"]
      : ["solid"];
  };

  remove: BackgroundRemovalService = async (
    input,
    signal,
    progress,
    options,
  ) => {
    signal.throwIfAborted();
    if (!options?.assetId || !/^asset_[a-zA-Z0-9_-]+$/.test(options.assetId))
      throw new Error("Upload the image before removing its background.");
    if (
      !input.size ||
      input.size > 20 * 1024 * 1024 ||
      !["image/png", "image/jpeg", "image/webp"].includes(input.type)
    )
      throw new Error("Choose a PNG, JPEG or WebP image smaller than 20 MB.");
    const base = this.baseUrl.replace(/\/+$/, "");
    if (base && !/^https?:\/\//i.test(new URL(base, location.href).href))
      throw new Error("Invalid background remover location.");
    const mode = options.mode || "solid";
    progress?.(
      mode === "photo" ? "Finding your subject…" : "Removing the background…",
    );
    const response = await fetch(
      `${base}/api/assets/${encodeURIComponent(options.assetId)}/background-removal`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...this.init.headers },
        body: JSON.stringify({
          mode,
          ...(options.tolerance !== undefined
            ? { tolerance: options.tolerance }
            : {}),
          ...(options.points?.length ? { points: options.points } : {}),
        }),
        credentials: this.init.credentials || "same-origin",
        redirect: "error",
        signal,
      },
    );
    if (!response.ok) {
      const body = (await response.json().catch(() => undefined)) as
        { error?: unknown } | undefined;
      throw new Error(
        response.status === 503 || response.status === 413
          ? typeof body?.error === "string"
            ? body.error
            : "Background removal is busy. Please retry."
          : "Background removal failed. Please retry or use a smaller image.",
      );
    }
    const blob = await response.blob();
    return blob.type === "image/png"
      ? blob
      : new Blob([blob], { type: "image/png" });
  };
}

export const createBackgroundRemover = (
  baseUrl?: string,
  init?: ConstructorParameters<typeof BackgroundRemover>[1],
): BackgroundRemovalService => {
  const remover = new BackgroundRemover(baseUrl, init);
  return Object.assign(
    (...args: Parameters<BackgroundRemovalService>) => remover.remove(...args),
    { modes: remover.modes },
  );
};
export const defaultBackgroundRemover = createBackgroundRemover();
